import { authenticationStateHandler } from "../authentication/logic/AuthenticationStateHandler";
import { LoginSuccessfulResponse } from "../authentication/model/LoginSuccessfulResponse";
import { ErrorCode } from "./errorCode";

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? ""

export class CallResult<T> {
  readonly result: T | null;
  readonly status: number | null;
  readonly errorType: ErrorType | null;
  readonly errorMessage: string | null;
  readonly errorResponse: ErrorResponse | null;

  constructor(result: T | null, status: number | null, type: ErrorType | null, errorMessage: string | null = null, errorResponse: ErrorResponse | null = null) {
    this.result = result;
    this.status = status;
    this.errorType = type;
    this.errorMessage = errorMessage;
    this.errorResponse = errorResponse;
  }

  getResultOrNull(): T | null {
    return this.result;
  }

  getStatusOrNull(): number | null {
    return this.status;
  }

  success(): boolean {
    return this.errorResponse == null;
  }
}

export enum ErrorType {
  AuthenticationError,
  AuthorizationError,
  ApiError,
  NetworkError,
  UnknownError,
}

export class ErrorResponse {
  message!: string;
  errorCode!: ErrorCode;
  statusCode!: number;
  traceId?: string;
  data?: any;
}

async function tryRefreshAccessToken(): Promise<boolean> {
  // We need to synchronize the refresh procedure accross tabs using navigator.lock.request().
  // Requests which are running in parallel trigger /api/auth/refresh in parallel but the
  // refresh token is valid only one time.
  return new Promise<boolean>((resolve) => {
    window.navigator.locks.request(
      "refresh_access_token",
      {
        mode: "exclusive",
        ifAvailable: true
      },
      async (lock) => {
        if (lock == null) {
          // In this case we just updated/tried to update the refresh token and do not have to do
          // it again. Therefore, we directly return isAuthenticated.
          // Although we have nested lock requests here, this is no deadlock because request() does
          // not block.
          window.navigator.locks.request(
            "refresh_access_token",
            { ifAvailable: false },
            (_) => resolve(authenticationStateHandler.isAuthenticated)
          );
          return;
        }

        const csrfToken = authenticationStateHandler.csrfToken;
        if (!csrfToken) {
          resolve(false);
          return;
        }

        const result = await doApiPost<null, LoginSuccessfulResponse>(
          "/api/auth/refresh", null,
          {
            handleUnauthenticated: false,
            headers: {
              "X-CSRF-Token": authenticationStateHandler.csrfToken ?? ""
            },
            credentials: "include"
          }
        )

        if (result.success() && result.result) {
          authenticationStateHandler.setAuthenticationTokens(
            result.result?.accessToken!,
            result.result?.csrfToken!
          )
          resolve(true);
          return;
        }
        else {
          resolve(false);
          return;
        }
      }
    )
  });
}

async function handleErrorResponse<T>(response: Response, bodyText: string): Promise<CallResult<T>> {
  let errorResponse: ErrorResponse | null = null;

  try {
    errorResponse = JSON.parse(bodyText);
  } catch (err) {
    errorResponse = {
      message: bodyText,
      errorCode: ErrorCode.GeneralError,
      statusCode: response.status,
    };
  }

  console.error(`Error ${response.status}: `, errorResponse ?? bodyText);

  return new CallResult<T>(null, response.status, ErrorType.ApiError, errorResponse?.message, errorResponse);
}

/**
 * Internal function to wrap all calls to the fetch(...) API. This is needed to
 * ensure that the response and error handling is done the same way for
 * GET, POST, ...
 *
 * This handles the 401 (not authenticated) response and tells the rest of the application
 * that we are not authenticated.
 *
 * raw: if true, do not try to parse the json and return the raw response as blob instead.
 *      T is expected to be Blob in this case.
 */
export async function fetchApiWrapper<T>(
  call: () => Promise<Response>,
  raw: boolean = false,
  handleUnauthenticated: boolean = true
): Promise<CallResult<T>> {
  try {
    const rawResponse = await call();

    if (rawResponse.status === 401) {
      if (handleUnauthenticated) {
        const success = await tryRefreshAccessToken();
        if (success) {
          return fetchApiWrapper(call, raw, false);
        }
        else {
          // Refreshing the access token did not work. Therefore, we need to logout
          authenticationStateHandler.setNotAuthenticated();
        }
      }
      return new CallResult<T>(null, 401, ErrorType.AuthenticationError);
    }

    if (rawResponse.status === 403) {
      return new CallResult<T>(null, 403, ErrorType.AuthorizationError);
    }

    if (!rawResponse.ok) {
      const bodyText = await rawResponse.text();
      return handleErrorResponse<T>(rawResponse, bodyText);
    }

    if (raw) {
      const blob = await rawResponse.blob();
      return new CallResult<T>(blob as T, rawResponse.status, null);
    }

    const bodyText = await rawResponse.text();
    return bodyText.length === 0
      ? new CallResult<T>(null, rawResponse.status, null)
      : new CallResult<T>(JSON.parse(bodyText) as T, rawResponse.status, null);
  }
  catch (err) {
    return new CallResult<T>(null, null, ErrorType.NetworkError, "Network failure");
  }
}

/**
 * Execute a GET API call.
 */
export async function doApiGet<T>(
  url: string,
  raw: boolean = false,
  handleUnauthenticated: boolean = true,
  options?: {
    accessToken?: string;
  }
): Promise<CallResult<T>> {
  return fetchApiWrapper(
    async () => {
      return await fetch(API_BASE_URL + url, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${options?.accessToken ?? authenticationStateHandler.accessToken}`
        }
      });
    },
    raw,
    handleUnauthenticated
  );
}

/**
 * Execute a POST API call.
 */
export async function doApiPost<TRequest, TResponse>(
  url: string,
  body: TRequest,
  options?: {
    raw?: boolean;
    handleUnauthenticated?: boolean;
    accessToken?: string;
    headers?: Record<string, string>;
    credentials?: RequestCredentials;
  }
): Promise<CallResult<TResponse>> {
  return fetchApiWrapper(
    async () =>
      await fetch(API_BASE_URL + url, {
        method: "POST",  // Kept as POST for this function
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${options?.accessToken ?? authenticationStateHandler.accessToken}`,
          ...options?.headers,
        },
        credentials: options?.credentials
      }),
    options?.raw ?? false,
    options?.handleUnauthenticated ?? true
  );
}

/**
 * Execute a PATCH API call.
 */
export async function doApiPatch<TRequest, TResponse>(
  url: string,
  body: TRequest,
  options?: {
    raw?: boolean;
    handleUnauthenticated?: boolean;
  }
): Promise<CallResult<TResponse>> {
  return fetchApiWrapper(
    async () =>
      await fetch(API_BASE_URL + url, {
        method: "PATCH",  // Kept as PATCH for this function
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authenticationStateHandler.accessToken}`,
        },
      }),
    options?.raw ?? false,
    options?.handleUnauthenticated ?? true
  );
}

/**
 * Execute a DELETE API call.
 */
export async function doApiDelete<TResponse>(
  url: string,
  options?: {
    raw?: boolean;
    handleUnauthenticated?: boolean;
  }
): Promise<CallResult<TResponse>> {
  return fetchApiWrapper(
    async () =>
      await fetch(API_BASE_URL + url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authenticationStateHandler.accessToken}`,
        },
      }),
    options?.raw ?? false,
    options?.handleUnauthenticated ?? true
  );
}
